/**
 * Producer talkback overlay: a speech bubble with a portrait chip, typed-on
 * text, and a spotlight (soft dim with holes + amber safe-frame rings) on the
 * console elements the current lesson is about.
 *
 * Text markup: `[1]` / `[R]` / `[SPACE]` render as key caps; `*word*` is highlighted.
 */
import './tutorial.css';
import { h, prefersReducedMotion } from '../ui/dom';

export interface SpotRect { x: number; y: number; w: number; h: number }

export interface Spot {
  /** Element(s) to light. Multiple elements in one spot are unioned into a single hole. */
  els: () => HTMLElement[];
  /** Key cap shown on the ring ("1", "R"…). */
  key?: string;
  /** Short caption on the ring ("PROGRAM", "BOOTH"). */
  label?: string;
}

export type DimLevel = 0 | 1 | 2;

export interface SayOptions {
  id: string;
  text: string;
  spots?: Spot[];
  /** 0 none · 1 soft (slow-mo) · 2 hold (frozen). Can be changed later with setDim. */
  dim?: DimLevel;
  /** Show a CONTINUE button (Enter / click). */
  ack?: string;
  step?: [number, number];
  /** Optional kicker above the text ("LESSON 2 · CUTTING"). */
  kicker?: string;
  /** Where to dock when there are no spots: 'center' (default) or 'pgm'. */
  dock?: 'center' | 'pgm';
}

export interface ProducerUI {
  readonly el: HTMLElement;
  say(o: SayOptions): void;
  /** Swap the bubble to a short "done" acknowledgement (green check), keeps position. */
  praise(text: string): void;
  /** Rewrite the current line without re-entering (e.g. woodwork mid-step). */
  retext(text: string): void;
  setDim(level: DimLevel): void;
  /** Status tag in the bubble footer, e.g. "HOLDING FOR YOU" / "SLOW-MO 0.25×". */
  setHold(label: string | null, kind?: 'hold' | 'slow'): void;
  hide(): void;
  /** Re-measure spot rects (call every frame; cheap when nothing moved). */
  frame(): void;
  /** Finish the typing animation immediately. Returns true if it was still typing. */
  flushTyping(): boolean;
  readonly visible: boolean;
  destroy(): void;
}

const GAP = 18;
const MARGIN = 12;
const CPS = 52; // typed characters per second

type Seg = { kind: 'text' | 'em'; text: string; el: HTMLElement } | { kind: 'key'; text: string; el: HTMLElement };

function parse(text: string): Seg[] {
  const out: Seg[] = [];
  const re = /\[([^\]]+)\]|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  const push = (kind: 'text' | 'em', t: string) => { if (t) out.push({ kind, text: t, el: h('span', { class: kind === 'em' ? 'sd-pd__em' : '' }) }); };
  while ((m = re.exec(text))) {
    push('text', text.slice(last, m.index));
    if (m[1] !== undefined) out.push({ kind: 'key', text: m[1], el: h('kbd', { class: 'sd-pd__kbd', text: m[1] }) });
    else push('em', m[2]!);
    last = m.index + m[0].length;
  }
  push('text', text.slice(last));
  return out;
}

function rectOf(els: HTMLElement[]): SpotRect | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) continue;
    x0 = Math.min(x0, r.left); y0 = Math.min(y0, r.top); x1 = Math.max(x1, r.right); y1 = Math.max(y1, r.bottom);
  }
  return x0 === Infinity ? null : { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function roundedRect(r: SpotRect, rad: number): string {
  const k = Math.min(rad, r.w / 2, r.h / 2);
  const { x, y, w, h: hh } = r;
  return `M${x + k},${y}H${x + w - k}A${k},${k} 0 0 1 ${x + w},${y + k}V${y + hh - k}A${k},${k} 0 0 1 ${x + w - k},${y + hh}H${x + k}A${k},${k} 0 0 1 ${x},${y + hh - k}V${y + k}A${k},${k} 0 0 1 ${x + k},${y}Z`;
}

const headsetSvg = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13v-1a8 8 0 0 1 16 0v1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><rect x="2.6" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/><rect x="17.2" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/><path d="M19.3 19c0 1.6-2 2.6-5.2 2.6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="13.4" cy="21.6" r="1.3" fill="currentColor"/></svg>`;

export function createProducerUI(root: HTMLElement, opts: { onAck(): void; onSkip(): void }): ProducerUI {
  const reduced = prefersReducedMotion();
  const layer = h('div', { class: 'sd-pd-layer', attrs: { 'data-ui': 'producer' } });
  const dim = h('div', { class: 'sd-pd-dim' });
  const rings = h('div', { class: 'sd-pd-rings' });

  const lamp = h('span', { class: 'sd-pd__lamp' });
  const wave = h('span', { class: 'sd-pd__wave' }, [h('i'), h('i'), h('i'), h('i'), h('i')]);
  const stepEl = h('span', { class: 'sd-pd__step' });
  const kickerEl = h('div', { class: 'sd-pd__kicker' });
  const body = h('div', { class: 'sd-pd__body' });
  const holdEl = h('span', { class: 'sd-pd__hold' });
  const ackBtn = h('button', { class: 'sd-pd__ack', attrs: { type: 'button' }, on: { click: (e) => { e.stopPropagation(); if (!flushTyping()) opts.onAck(); } } });
  const skipBtn = h('button', { class: 'sd-pd__skip', attrs: { type: 'button', title: 'Skip training' }, on: { click: (e) => { e.stopPropagation(); opts.onSkip(); } } }, [h('span', { text: 'SKIP' }), h('kbd', { text: 'ESC' })]);
  const caret = h('i', { class: 'sd-pd__caret' });
  const avatar = h('span', { class: 'sd-pd__avatar', html: headsetSvg });
  const bubble = h('div', { class: 'sd-pd', attrs: { role: 'dialog', 'aria-live': 'polite', 'aria-label': 'Producer talkback' }, on: { click: () => { flushTyping(); } } }, [
    h('div', { class: 'sd-pd__head' }, [
      h('span', { class: 'sd-pd__chip' }, [avatar, lamp]),
      h('span', { class: 'sd-pd__who' }, [h('b', { text: 'PRODUCER' }), h('span', { text: 'TALKBACK · CH 1' })]),
      wave,
      stepEl,
    ]),
    kickerEl,
    body,
    h('div', { class: 'sd-pd__foot' }, [holdEl, h('span', { class: 'sd-pd__spacer' }), skipBtn, ackBtn]),
    caret,
  ]);
  layer.append(dim, rings, bubble);
  root.append(layer);

  let spots: Spot[] = [];
  let ringEls: HTMLElement[] = [];
  let dimLevel: DimLevel = 0;
  let dock: 'center' | 'pgm' = 'center';
  let segs: Seg[] = [];
  let total = 0, shown = 0, typeStart = 0;
  let typing = false;
  let visible = false;
  let lastKey = '';
  let raf = 0;

  function setText(text: string, animate: boolean) {
    body.textContent = '';
    segs = parse(text);
    total = segs.reduce((n, s) => n + (s.kind === 'key' ? 1 : s.text.length), 0);
    for (const s of segs) body.append(s.el);
    shown = 0;
    typeStart = performance.now();
    typing = animate && !reduced;
    if (!typing) shown = total;
    renderTyping();
    bubble.dataset.state = typing ? 'typing' : 'waiting';
    if (typing) tick();
  }

  function renderTyping() {
    let left = shown;
    for (const s of segs) {
      if (s.kind === 'key') {
        const on = left > 0;
        s.el.classList.toggle('is-shown', on);
        if (on) left -= 1;
      } else {
        const n = Math.max(0, Math.min(s.text.length, left));
        s.el.textContent = s.text.slice(0, n);
        left -= n;
      }
    }
  }

  function tick() {
    cancelAnimationFrame(raf);
    const step = () => {
      if (!typing) return;
      shown = Math.min(total, Math.floor(((performance.now() - typeStart) / 1000) * CPS));
      renderTyping();
      if (shown >= total) { typing = false; bubble.dataset.state = 'waiting'; return; }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }

  function flushTyping(): boolean {
    if (!typing) return false;
    typing = false;
    shown = total;
    renderTyping();
    bubble.dataset.state = 'waiting';
    return true;
  }

  function buildRings() {
    rings.textContent = '';
    ringEls = spots.map((s) => {
      const r = h('div', { class: 'sd-pd-ring' }, [
        h('i', { class: 'sd-pd-ring__c sd-pd-ring__c--tl' }), h('i', { class: 'sd-pd-ring__c sd-pd-ring__c--tr' }),
        h('i', { class: 'sd-pd-ring__c sd-pd-ring__c--bl' }), h('i', { class: 'sd-pd-ring__c sd-pd-ring__c--br' }),
        s.key || s.label ? h('span', { class: 'sd-pd-ring__tag' }, [s.key ? h('kbd', { text: s.key }) : null, s.label ? h('span', { text: s.label }) : null]) : null,
      ]);
      rings.append(r);
      return r;
    });
  }

  function place(target: SpotRect | null) {
    const W = window.innerWidth, H = window.innerHeight;
    const bw = bubble.offsetWidth, bh = bubble.offsetHeight;
    let x: number, y: number, side: string;
    if (!target) {
      if (dock === 'pgm') {
        const pgm = document.querySelector<HTMLElement>('.sd-mon--pgm')?.getBoundingClientRect();
        if (pgm) { x = pgm.left + (pgm.width - bw) / 2; y = pgm.top + pgm.height * 0.52 - bh / 2; }
        else { x = (W - bw) / 2; y = (H - bh) / 2; }
      } else { x = (W - bw) / 2; y = H * 0.44 - bh / 2; }
      side = 'none';
    } else {
      const t = target;
      const fits = {
        right: t.x + t.w + GAP + bw <= W - MARGIN,
        left: t.x - GAP - bw >= MARGIN,
        below: t.y + t.h + GAP + bh <= H - MARGIN,
        above: t.y - GAP - bh >= MARGIN,
      };
      // Prefer the side with the most room among those that fit (wide targets → above/below).
      const order: (keyof typeof fits)[] = t.w > t.h * 2.2 ? ['above', 'below', 'right', 'left'] : ['right', 'left', 'above', 'below'];
      side = order.find((k) => fits[k]) ?? 'inside';
      const cx = t.x + t.w / 2, cy = t.y + t.h / 2;
      switch (side) {
        case 'right': x = t.x + t.w + GAP; y = cy - bh / 2; break;
        case 'left': x = t.x - GAP - bw; y = cy - bh / 2; break;
        case 'below': x = cx - bw / 2; y = t.y + t.h + GAP; break;
        case 'above': x = cx - bw / 2; y = t.y - GAP - bh; break;
        default: x = cx - bw / 2; y = t.y + t.h - bh - GAP; break;
      }
      x = Math.max(MARGIN, Math.min(W - bw - MARGIN, x));
      y = Math.max(MARGIN, Math.min(H - bh - MARGIN, y));
      // caret position along the facing edge
      if (side === 'right' || side === 'left') caret.style.setProperty('--cp', `${Math.max(18, Math.min(bh - 18, cy - y))}px`);
      else caret.style.setProperty('--cp', `${Math.max(22, Math.min(bw - 22, cx - x))}px`);
    }
    bubble.dataset.side = side;
    bubble.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  function frame() {
    if (!visible) return;
    const rects = spots.map((s) => rectOf(s.els()));
    const pad = 6;
    const padded = rects.map((r) => (r ? { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 } : null));
    const key = `${padded.map((r) => (r ? `${r.x | 0},${r.y | 0},${r.w | 0},${r.h | 0}` : '-')).join('|')}#${bubble.offsetWidth}x${bubble.offsetHeight}#${window.innerWidth}x${window.innerHeight}#${dimLevel}`;
    if (key === lastKey) return;
    lastKey = key;
    const TAG = 30; // room the ring's caption tag needs above the ring
    const tagInside: boolean[] = [];
    padded.forEach((r, i) => {
      const el = ringEls[i];
      if (!el) return;
      el.style.display = r ? '' : 'none';
      if (r) { el.style.transform = `translate(${r.x}px, ${r.y}px)`; el.style.width = `${r.w}px`; el.style.height = `${r.h}px`; }
      // Near the top of the screen the tag would sit on the top bar: tuck it inside the ring.
      const inside = !!r && r.y < 64;
      tagInside[i] = inside;
      el.classList.toggle('is-tag-inside', inside);
    });
    const W = window.innerWidth, H = window.innerHeight;
    const holes = padded.filter((r): r is SpotRect => !!r).map((r) => roundedRect(r, 8)).join('');
    dim.style.clipPath = `path(evenodd, 'M0,0H${W}V${H}H0Z${holes}')`;
    // Placement avoids the rings *and* their caption tags.
    const live = padded.map((r, i) => (r && spots[i] && (spots[i]!.key || spots[i]!.label) && !tagInside[i] ? { ...r, y: r.y - TAG, h: r.h + TAG } : r))
      .filter((r): r is SpotRect => !!r);
    const union = live.length ? live.reduce((a, b) => {
      const x0 = Math.min(a.x, b.x), y0 = Math.min(a.y, b.y);
      return { x: x0, y: y0, w: Math.max(a.x + a.w, b.x + b.w) - x0, h: Math.max(a.y + a.h, b.y + b.h) - y0 };
    }) : null;
    // Big unions (e.g. two tiles far apart) anchor on the first spot instead.
    place(live.length > 1 && union && union.w * union.h > window.innerWidth * window.innerHeight * 0.25 ? live[0]! : union);
  }

  const ui: ProducerUI = {
    el: layer,
    get visible() { return visible; },
    say(o) {
      spots = o.spots ?? [];
      dock = o.dock ?? 'center';
      buildRings();
      bubble.dataset.step = o.id;
      layer.dataset.step = o.id;
      bubble.classList.remove('is-praise');
      kickerEl.textContent = o.kicker ?? '';
      kickerEl.style.display = o.kicker ? '' : 'none';
      stepEl.textContent = o.step ? `${String(o.step[0]).padStart(2, '0')}/${String(o.step[1]).padStart(2, '0')}` : '';
      ackBtn.style.display = o.ack ? '' : 'none';
      if (o.ack) { ackBtn.textContent = ''; ackBtn.append(h('span', { text: o.ack }), h('kbd', { text: '⏎' })); }
      setText(o.text, true);
      ui.setDim(o.dim ?? 0);
      ui.setHold(null);
      lastKey = '';
      if (!visible) {
        visible = true;
        layer.classList.add('is-on');
      }
      // re-trigger the entrance
      bubble.classList.remove('is-in');
      void bubble.offsetWidth;
      bubble.classList.add('is-in');
      lamp.classList.add('is-talk');
      frame();
    },
    praise(text) {
      bubble.classList.add('is-praise');
      bubble.dataset.state = 'done';
      ackBtn.style.display = 'none';
      kickerEl.style.display = 'none';
      spots = [];
      buildRings();
      ui.setDim(0);
      ui.setHold(null);
      setText(`✓ ${text}`, true);
      lastKey = '';
      frame();
    },
    retext(text) { setText(text, true); lastKey = ''; },
    setDim(level) {
      dimLevel = level;
      layer.dataset.dim = String(level);
    },
    setHold(label, kind = 'hold') {
      holdEl.textContent = label ?? '';
      holdEl.dataset.kind = kind;
      holdEl.style.display = label ? '' : 'none';
    },
    hide() {
      if (!visible) return;
      visible = false;
      typing = false;
      layer.classList.remove('is-on');
      layer.dataset.dim = '0';
      delete layer.dataset.step;
      delete bubble.dataset.step;
      spots = [];
      buildRings();
    },
    frame,
    flushTyping,
    destroy() { cancelAnimationFrame(raf); layer.remove(); },
  };
  return ui;
}
